// Portal PDF download helper. Calls the portal-document-pdf edge
// function directly with the user's session token (not via
// supabase.functions.invoke — that path parses the body as JSON and
// would mangle the binary PDF stream). Streams the response into a
// Blob and triggers an attachment download via an object URL +
// synthetic anchor click. Same shape as PortalSpend's CSV export.
//
// Errors are returned, not thrown — callers can render their own
// inline error UI without try/catching.

import { supabase } from '@/lib/supabase'

type DocType = 'invoice' | 'quote'

interface DownloadResult { ok: boolean; error?: string }

export async function downloadPortalPdf(type: DocType, id: string): Promise<DownloadResult> {
  const { data: sessionData } = await supabase.auth.getSession()
  const accessToken = sessionData.session?.access_token
  if (!accessToken) return { ok: false, error: 'Not signed in' }

  // VITE_SUPABASE_URL is required at build time; the .functions.supabase.co
  // host derives from the project ref portion of it. Reusing the helper
  // means dev and prod share one code path.
  const projectUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined
  if (!projectUrl) return { ok: false, error: 'Missing Supabase URL' }
  // projectUrl shape: https://<ref>.supabase.co — functions live at
  // <ref>.functions.supabase.co, but the documented invoke route is also
  // <projectUrl>/functions/v1/<name>. We use the latter for environment
  // agreement.
  const url = `${projectUrl.replace(/\/$/, '')}/functions/v1/portal-document-pdf`

  let resp: Response
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ type, id }),
    })
  } catch (e) {
    return { ok: false, error: (e as Error).message || 'Network error' }
  }

  if (!resp.ok) {
    // Try to surface a useful message from a JSON error body.
    try {
      const j = await resp.json()
      return { ok: false, error: j?.error || `Request failed (${resp.status})` }
    } catch {
      return { ok: false, error: `Request failed (${resp.status})` }
    }
  }

  const blob = await resp.blob()
  // Filename comes from Content-Disposition; the server sets a sensible
  // one (e.g. INV-1234.pdf). Fall back if the header is unavailable.
  const cd = resp.headers.get('Content-Disposition') ?? ''
  const match = /filename="([^"]+)"/.exec(cd)
  const filename = match?.[1] || `document-${id}.pdf`

  const objUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(objUrl)
  return { ok: true }
}
